import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import SEOPageLayout from '../components/seo/SEOPageLayout';

const relatedLinks = [
  { to: '/retrouver-information-entreprise', label: 'Pourquoi retrouver une information prend autant de temps' },
  { to: '/centraliser-information-entreprise', label: 'Pourquoi centraliser ne suffit pas' },
  { to: '/clone-collaborateur-entreprise', label: 'Cloner la mémoire d\'un collaborateur' },
];

const RetrouperDocument = () => {
  return (
    <SEOPageLayout
      title="Retrouver une information dans un document d'entreprise : pourquoi c'est si difficile"
      metaTitle="Retrouver une information dans un document | Cloneia"
      metaDescription="Vos documents contiennent des réponses. Mais personne ne les retrouve. Découvrez comment transformer vos fichiers en connaissance accessible."
      url="/retrouver-information-document-entreprise"
      readingTime={4}
      subtitle="Vos documents ne sont plus des fichiers. Ils deviennent des réponses."
      relatedLinks={relatedLinks}
    >
      <article className="space-y-12 text-[#a1a1aa] leading-relaxed" data-testid="seo-article-document">

        {/* Section 1 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Des centaines de documents. Aucune réponse.
          </h2>
          <p>
            Votre entreprise produit des documents chaque jour. Comptes rendus, propositions commerciales, contrats, notes internes, procédures, rapports.
          </p>
          <p className="mt-4">
            Ces documents contiennent une quantité massive d'information. Des décisions, des engagements, des détails techniques, des historiques.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Et pourtant, quand vous avez besoin d'une information précise, vous ne la retrouvez pas.
          </p>
        </motion.div>

        {/* Section 2 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Le problème : chercher dans un fichier n'est pas chercher une réponse
          </h2>
          <p>
            La recherche classique dans un document fonctionne par mots-clés. Vous tapez un terme, vous trouvez les occurrences.
          </p>
          <p className="mt-4">
            Mais ce n'est pas ce dont vous avez besoin. Vous ne cherchez pas un mot. Vous cherchez une réponse.
          </p>
          <div className="mt-6 p-6 rounded-xl border border-white/5 bg-[#1a1a2e]/30">
            <p className="text-[#f5f5f5] font-medium mb-2">La différence</p>
            <p>
              "Quel délai de livraison a été convenu avec le client Morel ?" — Cette réponse se trouve peut-être dans un email, un contrat, ou un compte rendu de réunion. La recherche par mot-clé ne vous aidera pas. Il faut comprendre le contenu, pas juste le scanner.
            </p>
          </div>
        </motion.div>

        {/* Section 3 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Des fichiers partout, du sens nulle part
          </h2>
          <p>
            Le problème s'aggrave avec le temps. Plus votre entreprise grandit, plus les documents s'accumulent. Ils sont rangés dans des dossiers, des drives, des espaces partagés.
          </p>
          <p className="mt-4">
            Personne ne sait exactement ce qui se trouve où. Et même si vous trouvez le bon fichier, il faut encore le lire, le comprendre, en extraire ce qui vous intéresse.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Résultat : des heures perdues à fouiller dans des fichiers au lieu de travailler.
          </p>
        </motion.div>

        {/* Section 4 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Ce que vos documents devraient être
          </h2>
          <p>
            Un document ne devrait pas être un fichier qu'on ouvre et qu'on lit. Il devrait être une source de réponses.
          </p>
          <p className="mt-4">
            Vous devriez pouvoir poser une question — "Quelles sont les conditions du contrat signé en mars ?" — et obtenir la réponse. Sans ouvrir le fichier. Sans chercher dans les dossiers.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Vos documents ne sont plus des fichiers. Ils deviennent des réponses.
          </p>
        </motion.div>

        {/* Section 5 - Cloneia */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Comment Cloneia transforme vos documents
          </h2>
          <p>
            Cloneia crée un clone mémoire de chaque collaborateur. Ce clone intègre non seulement les échanges (emails, appels, messages), mais aussi les documents partagés et produits.
          </p>
          <p className="mt-4">
            Chaque document devient une source de connaissance interrogeable. Vous posez la question, vous obtenez la réponse — avec la source, le contexte, et les détails.
          </p>
          <div className="mt-6 space-y-4">
            <div className="flex gap-4 items-start">
              <span className="text-[#3b82f6] font-bold text-lg mt-0.5">1.</span>
              <div>
                <p className="text-[#f5f5f5] font-medium">Contrats et engagements</p>
                <p className="text-sm mt-1">Retrouvez instantanément une clause, un délai, un montant convenu.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <span className="text-[#3b82f6] font-bold text-lg mt-0.5">2.</span>
              <div>
                <p className="text-[#f5f5f5] font-medium">Comptes rendus et décisions</p>
                <p className="text-sm mt-1">Retrouvez qui a décidé quoi, quand, et dans quel contexte.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <span className="text-[#3b82f6] font-bold text-lg mt-0.5">3.</span>
              <div>
                <p className="text-[#f5f5f5] font-medium">Procédures et savoir-faire</p>
                <p className="text-sm mt-1">Accédez aux process internes sans chercher dans les dossiers.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 6 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            La fin de la recherche dans les fichiers
          </h2>
          <p>
            Avec Cloneia, vous ne cherchez plus dans les documents. Vous posez la question. Le système parcourt l'ensemble des documents, des échanges, et de la connaissance de vos équipes pour vous donner la réponse.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Une question. Une réponse. Peu importe dans quel document elle se trouvait.
          </p>
        </motion.div>

      </article>
    </SEOPageLayout>
  );
};

export default RetrouperDocument;
